/**
 * Accounts Store
 * Manages account summary, subscriptions, and payments data
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { servicesContainer } from '@/core/di/services-container';
import { AccountSummary, Subscription, PaymentToday } from '../types/dashboard-types';

interface AccountsState {
  // Summary data
  summary: AccountSummary | null;

  // Subscriptions
  subscriptions: Subscription[];
  subscriptionsTotalCount: number;
  subscriptionsTotalPages: number;
  subscriptionsCurrentPage: number;
  subscriptionsPageSize: number;
  expiringCount: number;

  // Payments
  paymentsToday: PaymentToday[];
  paymentsTotalAmount: number;
  paymentsFailedCount: number;

  // Loading states
  isLoadingSummary: boolean;
  isLoadingSubscriptions: boolean;
  isLoadingPayments: boolean;

  // Error states
  summaryError: string | null;
  subscriptionsError: string | null;
  paymentsError: string | null;

  // Last updated
  lastUpdated: Date | null;

  // Actions
  loadSummary: () => Promise<void>;
  loadSubscriptions: () => Promise<void>;
  loadSubscriptionsPage: (page: number) => Promise<void>;
  setSubscriptionsPageSize: (size: number) => Promise<void>;
  loadPaymentsToday: () => Promise<void>;
  loadAll: () => Promise<void>;
  refresh: () => Promise<void>;
  reset: () => void;
}

const initialState = {
  summary: null,
  subscriptions: [],
  subscriptionsTotalCount: 0,
  subscriptionsTotalPages: 0,
  subscriptionsCurrentPage: 0,
  subscriptionsPageSize: 10,
  expiringCount: 0,
  paymentsToday: [],
  paymentsTotalAmount: 0,
  paymentsFailedCount: 0,
  isLoadingSummary: false,
  isLoadingSubscriptions: false,
  isLoadingPayments: false,
  summaryError: null,
  subscriptionsError: null,
  paymentsError: null,
  lastUpdated: null,
};

export const useAccountsStore = create<AccountsState>()(
  persist(
    (set, get) => ({
      ...initialState,

      loadSummary: async () => {
        set({ isLoadingSummary: true, summaryError: null });

        try {
          const services = servicesContainer.getServices();
          const result = await services.getAccountsUseCase.executeSummary();

          if (result.hasError) {
            set({
              summaryError: result.errorMessage,
              isLoadingSummary: false,
            });
            return;
          }

          set({
            summary: result.summary,
            isLoadingSummary: false,
            lastUpdated: new Date(),
          });
        } catch (error) {
          set({
            summaryError: error instanceof Error ? error.message : 'Failed to load account summary',
            isLoadingSummary: false,
          });
        }
      },

      loadSubscriptions: async () => {
        const { subscriptionsCurrentPage, subscriptionsPageSize } = get();
        set({ isLoadingSubscriptions: true, subscriptionsError: null });

        try {
          const services = servicesContainer.getServices();
          const result = await services.getAccountsUseCase.executeSubscriptions(
            subscriptionsCurrentPage,
            subscriptionsPageSize
          );

          if (result.hasError) {
            set({
              subscriptionsError: result.errorMessage,
              isLoadingSubscriptions: false,
            });
            return;
          }

          const totalPages = Math.ceil(result.totalCount / subscriptionsPageSize);

          set({
            subscriptions: result.subscriptions,
            subscriptionsTotalCount: result.totalCount,
            subscriptionsTotalPages: totalPages,
            expiringCount: result.expiringCount,
            isLoadingSubscriptions: false,
          });
        } catch (error) {
          set({
            subscriptionsError: error instanceof Error ? error.message : 'Failed to load subscriptions',
            isLoadingSubscriptions: false,
          });
        }
      },

      loadSubscriptionsPage: async (page: number) => {
        set({ subscriptionsCurrentPage: page });
        await get().loadSubscriptions();
      },

      setSubscriptionsPageSize: async (size: number) => {
        set({ subscriptionsPageSize: size, subscriptionsCurrentPage: 0 });
        await get().loadSubscriptions();
      },

      loadPaymentsToday: async () => {
        set({ isLoadingPayments: true, paymentsError: null });

        try {
          const services = servicesContainer.getServices();
          const result = await services.getAccountsUseCase.executePaymentsToday();

          if (result.hasError) {
            set({
              paymentsError: result.errorMessage,
              isLoadingPayments: false,
            });
            return;
          }

          set({
            paymentsToday: result.payments,
            paymentsTotalAmount: result.totalAmount,
            paymentsFailedCount: result.failedCount,
            isLoadingPayments: false,
          });
        } catch (error) {
          set({
            paymentsError: error instanceof Error ? error.message : 'Failed to load payments',
            isLoadingPayments: false,
          });
        }
      },

      loadAll: async () => {
        await Promise.all([
          get().loadSummary(),
          get().loadSubscriptions(),
          get().loadPaymentsToday(),
        ]);
      },

      refresh: async () => {
        await get().loadAll();
      },

      reset: () => {
        set(initialState);
      },
    }),
    {
      name: 'accounts-storage',
      partialize: () => ({}), // Don't persist any state
    }
  )
);
